import React from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Icon from '../Icon';
import Button from '../Button';
import Input from '../Input';
import Loader from '../Loader';
import Dimmer from '../Dimmer';
import Header from '../Header';
import Form from '../Form';
import Grid from '../Grid';
import Message from '../Message';
import PasswordRules from '../PasswordRules';
import messages from './messages';
import {
  missingValueLabel,
  missingPasswordLabel,
  missingNewPasswordLabel,
  missingConfirmPasswordLabel,
  passwordMismatchLabel,
  passwordNotSecureLabel,
  emailFormatLabel,
} from './labelComponents';
import './styles.css';
import {
  renderHEInstitutionProfileModal,
  renderHSInstitutionProfileModal,
  renderUserProfileModal,
} from '../../utils/counselorCommunityModals';

function validateForm(formData) {
  let secure = true;
  if (formData.newPassword) {
    const regEx = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\]\-^$*.[()?"!@#%&/,><':;|_~`])[A-Za-z\d\]\-^$*.[()?"!@#%&/,><':;|_~`]{8,}/;
    secure = regEx.test(formData.newPassword);
  }
  let emailFormat = true;
  if (formData.email) {
    const regEx = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;
    emailFormat = regEx.test(formData.email);
  }
  return {
    missingFirstName: !formData.firstName,
    missingLastName: !formData.lastName,
    missingEmail: !formData.email,
    missingPassword: !formData.password && (formData.newPassword || formData.confirmPassword),
    missingNewPassword: formData.password && !formData.newPassword,
    missingConfirmPassword: formData.password && !formData.confirmPassword,
    passwordMismatch: formData.newPassword !== formData.confirmPassword,
    passwordNotSecure: !secure,
    formatErrorEmail: !emailFormat,
  };
}

export const elementIds = {
  firstNameId: 'user-form-first-name',
  lastNameId: 'user-form-last-name',
  emailId: 'user-form-email',
  currentPasswordId: 'current-password-input',
  newPasswordId: 'new-password-input',
  confirmPasswordId: 'confirm-password-input',
};

class ChangeProfileForm extends React.PureComponent {
  static propTypes = {
    user: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }).isRequired,
    requesting: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    updateError: PropTypes.shape({
      generalError: PropTypes.bool,
      userExists: PropTypes.bool,
    }).isRequired,
    isCommunityServerlessOn: PropTypes.bool,
    uuid: PropTypes.string,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }),
    current: PropTypes.shape({
      viewer: PropTypes.object,
    }),
  };

  constructor(props) {
    super(props);
    this.state = {
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      email: props.user.email,
      errors: {},
      showUserModal: false,
      showCollegeModal: false,
      showHighschoolModal: false,
      userId: null,
      collegeId: null,
      highschoolId: null,
      idType: null,
    };
  }

  getModifiedFormFields = (formData) => {
    const modifiedFormFields = {};
    /* istanbul ignore else */
    if (formData.firstName) {
      if (!this.props.user.firstName || formData.firstName.trim() !== this.props.user.firstName.trim()) {
        modifiedFormFields.firstName = formData.firstName.trim();
      }
    }
    /* istanbul ignore else */
    if (formData.lastName) {
      if (!this.props.user.lastName || formData.lastName.trim() !== this.props.user.lastName.trim()) {
        modifiedFormFields.lastName = formData.lastName.trim();
      }
    }
    /* istanbul ignore else */
    if (formData.email) {
      if (!this.props.user.email || formData.email.trim() !== this.props.user.email.trim()) {
        modifiedFormFields.email = formData.email.trim();
      }
    }
    if (formData.password) {
      modifiedFormFields.password = formData.password;
    }
    if (formData.newPassword) {
      modifiedFormFields.newPassword = formData.newPassword;
    }
    return modifiedFormFields;
  };

  _onChange = (e, { value, name } = {}) => {
    value = value || e.target.value;
    name = name || e.target.name;
    this.setState({ [name]: value });
  };

  _onSubmit = (e) => {
    const formData = omit(this.state, 'errors');
    e.preventDefault();
    const errors = validateForm(formData);
    this.setState({ errors });
    if (!Object.keys(errors).some((key) => errors[key])) {
      const modifiedFormFields = this.getModifiedFormFields(formData);
      this.props.onSave(modifiedFormFields);
    }
  };

  closeModals = (modalOptions) => this.setState({ ...modalOptions });

  handleViewStaff = (event, institutionName, institutionType, institutionSubType) => {
    event?.preventDefault();
    this.props.history.push('/counselorcommunity/search/people?' +
      `viewStaffInstitutionName=${institutionName}&` +
      `viewStaffInstitutionType=${institutionType}&` +
      `viewStaffInstitutionSubType=${institutionSubType}`);
    window.dispatchEvent(new Event('counselor-community-serverless-update-route'));
  };

  openInstitutionProfileModal = ({ collegeId, highschoolId, idType }) => {
    this.closeModals();
    this.setState({
      showCollegeModal: !!collegeId,
      showHighschoolModal: !!highschoolId,
      collegeId,
      highschoolId,
      idType,
    });
  };

  render() {
    const {
      errors,
      firstName,
      lastName,
      email,
      showUserModal,
      showCollegeModal,
      idType,
      collegeId,
      showHighschoolModal,
      highschoolId,
    } = this.state;
    const { updateError={}, requesting, isCommunityServerlessOn, uuid } = this.props;
    const { firstNameId, lastNameId, emailId, currentPasswordId, newPasswordId, confirmPasswordId } = elementIds;
    const firstNameErrorLabel = errors.missingFirstName ? missingValueLabel : '';
    const lastNameErrorLabel = errors.missingLastName ? missingValueLabel : '';
    const emailErrorLabel = errors.missingEmail ? missingValueLabel : '';
    const emailFormatErrorLabel = errors.formatErrorEmail ? emailFormatLabel : '';
    const passwordErrorLabel = errors.missingPassword ? missingPasswordLabel : '';
    const newPasswordErrorLabel = errors.missingNewPassword ? missingNewPasswordLabel : '';
    const confirmPasswordErrorLabel = errors.missingConfirmPassword ? missingConfirmPasswordLabel : '';
    const passwordMismatchErrorLabel = errors.passwordMismatch ? passwordMismatchLabel : '';
    const passwordNotSecureErrorLabel = errors.passwordNotSecure ? passwordNotSecureLabel : '';

    const errorMessage = Object.keys(updateError).map((errorKey) => (
      <Message role="alert" negative key={errorKey}>
        <Message.Header>
          <FormattedMessage {...messages[errorKey]} />
        </Message.Header>
      </Message>
    ));
    return (
      <React.Fragment>
        {renderUserProfileModal(showUserModal, uuid, this.props, this.closeModals, this.openInstitutionProfileModal)}
        {renderHEInstitutionProfileModal(showCollegeModal, idType, collegeId, this.props, this.closeModals, this.handleViewStaff)}
        {renderHSInstitutionProfileModal(showHighschoolModal, this.props, highschoolId, idType, this.closeModals, this.handleViewStaff)}
        <Dimmer.Dimmable as={Form} id="form-update-user" onSubmit={this._onSubmit} size="large">
          <Dimmer
            role="progressbar"
            aria-describedby="Updating User..."
            aria-busy={requesting}
            inverted
            active={requesting}
          >
            <Loader active={requesting} />
          </Dimmer>
          <fieldset styleName="change-profile-fieldset">
            <legend styleName="go-to-profile-legend">
              <Header as="h3" styleName="form-section-heading">
                <FormattedMessage {...messages.yourName} />
              </Header>
            </legend>
            <Grid>
              <Grid.Column width={5}>
                <Grid.Row>
                  <Form.Field styleName="change-profile-form-field">
                    <label htmlFor={firstNameId}>
                      <FormattedMessage {...messages.firstName} />
                    </label>
                    <Input type="text" name="firstName" value={firstName} onChange={this._onChange}>
                      <input id={firstNameId} required aria-invalid={!!firstNameErrorLabel} />
                    </Input>
                    {firstNameErrorLabel}
                  </Form.Field>
                </Grid.Row>
              </Grid.Column>
              <Grid.Column width={5}>
                <Grid.Row>
                  <Form.Field styleName="change-profile-form-field">
                    <label htmlFor={lastNameId}>
                      <FormattedMessage {...messages.lastName} />
                    </label>
                    <Input type="text" name="lastName" value={lastName} onChange={this._onChange}>
                      <input id={lastNameId} required aria-invalid={!!lastNameErrorLabel} />
                    </Input>
                    {lastNameErrorLabel}
                  </Form.Field>
                </Grid.Row>
              </Grid.Column>
            </Grid>
            <p styleName="go-to-profile-message">
              <FormattedMessage
                {...messages.gotoYourProfile}
                values={{
                  yourProfile: isCommunityServerlessOn ? (
                    <Link
                      to={{
                        pathname: '/settings/change-profile',
                        state: { lastUrlBeforeVisited: window.location.pathname },
                      }}
                      className="user-profile-link"
                      onClick={() => this.setState({ showUserModal: true })}
                    >
                      <FormattedMessage {...messages.yourProfile} />
                    </Link>
                  ) :
                    <Link to="/counselor-community/profile"><FormattedMessage {...messages.yourProfile} /></Link>,
                }}
              />
            </p>
          </fieldset>
          <fieldset styleName="change-profile-fieldset">
            <legend styleName="go-to-profile-legend">
              <Header as="h3" styleName="form-section-heading">
                <FormattedMessage {...messages.contactInformation} />
              </Header>
            </legend>
            <Form.Field styleName="change-profile-form-field">
              <label htmlFor={emailId}>
                <FormattedMessage {...messages.email} />
              </label>
              <Input type="email" name="email" value={email} onChange={this._onChange}>
                <input
                  id={emailId}
                  aria-required
                  required
                  autoComplete="off"
                  aria-invalid={!!emailErrorLabel || !!emailFormatErrorLabel}
                />
              </Input>
              {emailErrorLabel}
              {emailFormatErrorLabel}
            </Form.Field>
          </fieldset>
          <fieldset styleName="change-profile-fieldset">
            <legend styleName="go-to-profile-legend">
              <Header as="h3" styleName="form-section-heading form-section-heading-extra-margin">
                <FormattedMessage {...messages.changePasswordSection} />
              </Header>
            </legend>
            <Form.Field styleName="change-profile-form-field">
              <label htmlFor={currentPasswordId}>
                <FormattedMessage {...messages.password} />
              </label>
              <Input name="password" icon iconPosition="left" onChange={this._onChange}>
                <Icon name="lock" />
                <input
                  type="password"
                  id={currentPasswordId}
                  autoComplete="off"
                  aria-invalid={!!passwordErrorLabel}
                />
              </Input>
              {passwordErrorLabel}
            </Form.Field>
            <Form.Field styleName="change-profile-form-field">
              <label htmlFor={newPasswordId}>
                <FormattedMessage {...messages.newPassword} />
              </label>
              <Input name="newPassword" icon iconPosition="left" onChange={this._onChange}>
                <Icon name="lock" />
                <input
                  type="password"
                  id={newPasswordId}
                  autoComplete="off"
                  aria-invalid={!!newPasswordErrorLabel || !!passwordNotSecureErrorLabel}
                />
              </Input>
              {newPasswordErrorLabel}
              {passwordNotSecureErrorLabel}
              <PasswordRules />
            </Form.Field>
            <Form.Field styleName="change-profile-form-field">
              <label htmlFor={confirmPasswordId}>
                <FormattedMessage {...messages.confirmPassword} />
              </label>
              <Input name="confirmPassword" icon iconPosition="left" onChange={this._onChange}>
                <Icon name="lock" />
                <input
                  type="password"
                  id={confirmPasswordId}
                  autoComplete="off"
                  aria-invalid={!!confirmPasswordErrorLabel || !!passwordMismatchErrorLabel}
                />
              </Input>
              {confirmPasswordErrorLabel}
              {passwordMismatchErrorLabel}
            </Form.Field>
            {errorMessage}

            <div styleName="button-section">
              <Button data-cy="change-profile-save-btn" primary type="submit">
                <Icon name="checkmark" />
                <FormattedMessage {...messages.buttonSave} />
              </Button>
            </div>
          </fieldset>
        </Dimmer.Dimmable>
      </React.Fragment>
    );
  }
}

export default ChangeProfileForm;
